import { Network, DataSet } from "vis-network/standalone/umd/vis-network"
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom'
import { useQuery } from 'Hooks'
import {
  expandAbstractNode,
  toggleRelationLabels
} from 'Services/GraphUtils'
import { getAttributeNode, getRelationNode, getUid, splitUid } from 'Services/WeaverUtils'
import CreateRelationModal from './CreateRelationModal'
import RenameRelationModal from './RenameRelationModal'
import GraphFilterButton from './GraphFilterButton'
import FreezeGraphButton from "./FreezeGraphButton"
import { usePrevious } from 'Hooks'
import { connect, useDispatch } from 'react-redux'
import { Col, Layout, Row } from 'antd'
import { useEffect, useLayoutEffect, useState, useRef } from 'react'
import { updateGraphNodes, setGraphNodes } from 'Actions/graph'
import {
  setGraphAddingEdge,
  setGraphCreatingEdge,
  setShowAttributes,
  setShowAllMetaNodes,
  setShowRelevantMetaNodes,
  setQueryResults
} from '../../Context/actions'
import { setFilteredNodesBySearch, setHideRelationLabels } from 'Actions/graphFilter'
import { setTab, setCurrentItem, setTabDisabled } from 'Actions/explore'
import { NODE, RELATION, RELATION_NODE, ATTRIBUTES, ATTRIBUTE_VALUE, ATTRIBUTE_NODE, VIEWTYPE } from 'Types'
import { useGraphData } from "../../Context"
import styled from 'styled-components'
import './Graph.scss'
import SearchGraphButton from "./SearchGraphButton"
import { createRelationInGraph, getColorByGraph, getColorByType } from "../Utils"

const defaultOptions = (darkMode, groups) => {
  return {
    physics: {
      stabilization: false,
      solver: 'forceAtlas2Based',
      forceAtlas2Based: {
        theta: 0.75,
        damping: 1,
      }
    },
    autoResize: true,
    layout: {
      hierarchical: false
    },
    interaction: {
      hover: true,
      multiselect: true
    },
    nodes: {
      shape: 'dot',
      size: 10,
      borderWidth: 2,
      font: {
        face: 'Raleway',
        size: 12,
        color: darkMode && '#bbc4d4' || 'rgb(0,0,0)',
        strokeWidth: 1,
        strokeColor: darkMode && '#000' || '#fff',
      }
    },
    edges: {
      smooth: true,
      arrowStrikethrough: false,
      color: {
        inherit: 'both'
      },
      font: {
        color: darkMode && '#dae3ed' || 'rgb(0,0,0)',
        face: 'Avenir',
        size: 10,
        strokeColor: darkMode && 'rgb(6,6,6)' || '#fff'
      },
      arrows: {
        to: {
          enabled: true,
          scaleFactor: 0.5
        }
      }
    },
    groups
  }
}

const GraphViewContainer = styled(Layout)`
  height: 100%;
  position: relative;
  background: transparent;
  & > .ant-row {
    pointer-events: none;
    margin: 16px !important;
    z-index: 2;
    position: absolute;
    & > .ant-col {
      pointer-events: auto;
      & > * {
        background: transparent;
      }
      & > .is-open {
        background: var(--background);
      }
    }
  }
`;

export function Graph({
  activeItems,
  currentTab,
  darkModeEnabled,
  graphData: {
    nodes,
    edges,
    groups
  },
  hideRelationLabels,
  memberKey,
  setTab,
  setHideRelationLabels,
  setCurrentItem,
  setTabDisabled,
  tabsDisabled,
  tabIsLocked,
  updateGraphNodes,
  viewType,
  nameKeys
}) {

  const dispatch = useDispatch()
  const [graphCtx, ctxDispatch] = useGraphData()
  const { visToWvrMap } = graphCtx
  const {
    colorPickerProps,
    creatingEdgeProps,
    isAddingEdge,
    renameRelationProps,
  } = graphCtx
  const history = useHistory()
  const params = useQuery()
  const { url } = useRouteMatch()
  const { hash } = useLocation()

  const [network, addNetwork] = useState(null);
  const [nodeSet, setNodeSet] = useState(null);
  const [edgeSet, setEdgeSet] = useState(null);
  const ref = useRef(null);

  const [isFrozen, setIsFrozen] = useState(false);

  const [colorByGraph, setColorByGraph] = useState(JSON.parse(localStorage.getItem('colorByGraph')));


  useEffect(() => {
    colorByGraph ? dispatch(updateGraphNodes(getColorByGraph(nodes))) : dispatch(updateGraphNodes(getColorByType(nodes)))
  }, [colorByGraph])

  const graphFiltersMap = {
    colorByGraph: (val) => {
      setColorByGraph(val)
      localStorage.setItem('colorByGraph', val)
    },
    hideRelationLabels: setHideRelationLabels,
    showAttributes: (value) => {
      localStorage.setItem('showAttributes', value)
      ctxDispatch(setShowAttributes(value))
    },
    showRelevantMetaNodes: (value) => {
      localStorage.setItem('showRelevantMetaNodes', value)
      ctxDispatch(setShowRelevantMetaNodes(value))
    },
    showAllMetaNodes: (value) => {
      localStorage.setItem('showAllMetaNodes', value)
      ctxDispatch(setShowAllMetaNodes(value))
    }
  }

  const [active, setActive] = activeItems;

  const prevGraphData = usePrevious({ nodes, edges })

  const setEdgeLabelVisility = (id, visible) => {
    if (hideRelationLabels) {
      updateGraphNodes(toggleRelationLabels({ [id]: edges[id] }, !visible))
    }
  }

  const handleTabChange = (value) => {
    if (!tabIsLocked && (currentTab !== value)) {
      params.set('tab', value)
      history.replace({ pathname: url, search: params.toString(), hash })
      setTab(value)
    }
  }

  const handleEdgeClick = async (id, es) => {
    const edge = es.get(id);
    const { wvrId, entityType } = edge;

    setActive({ id, wvrId, entityType, type: RELATION, viewType });

    const relation =
      viewType === VIEWTYPE.ABSTRACT
        ? edge
        : entityType === ATTRIBUTE_NODE
          ? await getAttributeNode(wvrId)
          : await getRelationNode(wvrId);

    if (!relation.noAttributes) {
      setCurrentItem({
        ...(entityType === ATTRIBUTE_NODE ? relation : { relation }),
        entityType,
        wvrId,
        type: RELATION,
        viewType,
      });
    }
  };

  const handleNodeClick = async (id, ns) => {
    const node = ns.get(id)
    setActive({
      ...node,
      type: NODE
    })

    setCurrentItem({
      ...node,
      type: ATTRIBUTES,
      memberKey,
      viewType
    })
  }

  const handleClickEvt = async ({ nodes, edges }, ns, es) => {
    let id;
    if (nodes[0]) {
      id = nodes[0]
      await handleNodeClick(id, ns)
      handleTabChange('attributes')
    } else if (edges[0]) {
      id = edges[0]
      handleEdgeClick(id, es)
      handleTabChange('attributes')
    } else {
      ctxDispatch(setGraphAddingEdge(false))
    }

    if (id && tabsDisabled.attributes) {
      setTabDisabled({ key: 'attributes', disabled: false })
    }
    setActive(undefined)
  }

  const expandNode = async (uid) => {
    const [id, graph] = splitUid(uid)
    const query = new Weaver.Query()
      .restrict(id)
      .inGraph(graph)
      .selectIn('*')
      .peekIn()
      .alwaysLoadAttributes(nameKeys)

    const results = await query
      .withAttributes()
      .withRelations()
      .setMetaNodesQuery(new Weaver.Query().selectIn('*').selectOut('*'))
      .find()
    ctxDispatch(setQueryResults(results))
  };

  const handleDblClickEvt = async ({ nodes }, nodeSet) => {
    if (_.isEmpty(nodes[0])) {
      return
    } else {
      const id = nodeSet.get(nodes[0]).wvrId
      if (viewType === VIEWTYPE.ABSTRACT) {
        await expandAbstractNode(id)
      } else {
        await expandNode(id)
      }
    }
  };

  const ctxMenuCb = ({ event, nodes, edges, target, network }) => {
    if (target === NODE || target === ATTRIBUTE_NODE || target === RELATION_NODE) {
      setActive({
        nodes,
        type: NODE,
        event,
        network
      })
    } else if (target === ATTRIBUTE_VALUE) {
      setActive({
        edges,
        nodes,
        type: ATTRIBUTE_VALUE,
        event,
        network
      })
    } else if (target === RELATION) {
      setActive({
        edges,
        nodes,
        type: RELATION,
        event,
        network
      })
    } else {
      setActive(undefined)
    }
  }

  const internalEvents = (network, nodeSet, edgeSet) => ({
    hoverEdge: ({ edge }) => setEdgeLabelVisility(edge, true),
    blurEdge: ({ edge }) => setEdgeLabelVisility(edge, false),
    dragEnd: ({ nodes }) => {
      const toPin = nodeSet.get(nodes).map((target) => {
        return { ...target, physics: false }
      })
      nodeSet.update(toPin)
    },
    oncontext: ({ event, pointer, nodes, edges }) => {
      let target = undefined
      event.preventDefault()
      const { x, y } = pointer.DOM
      const node = network.getNodeAt({ x, y })
      const edge = network.getEdgeAt({ x, y })

      if (node && !nodes.includes(node)) {
        nodes = [node]
        edges = []
      }

      if ((edge && !edges.includes(edge)) && !node) {
        edges = [edge]
        nodes = []
      }

      network.setSelection({
        nodes,
        edges
      }, {
        highlightEdges: false
      })

      if (node) {
        target = nodeSet.get(node).entityType
      } else if (edge) {
        target = RELATION
      }

      return ctxMenuCb({ event, pointer, nodes, edges, target, network: network })
    },
  })

  const mergedOptions = {
    ..._.merge(
      defaultOptions(darkModeEnabled, groups),
      {
        physics: {
          forceAtlas2Based: {
            gravitationalConstant: viewType === VIEWTYPE.ABSTRACT ? -75 : -50
          }
        }
      }
    ),
    manipulation: {
      enabled: false,
      addEdge: async ({ from, to }, callback) => {
        const edgeData = {
          from: getUid(visToWvrMap[from]),
          to: getUid(visToWvrMap[to])
        }

        if (to) {
          const promise = new Promise((resolve, reject) => {
            ctxDispatch(setGraphCreatingEdge({
              ...creatingEdgeProps,
              edgeData,
              promise: {
                resolve,
                reject
              }
            }))
          })

          const { sources, relKeys, targets } = await promise

          const results = await createRelationInGraph(relKeys, sources, targets)
          ctxDispatch(setQueryResults(results))
          ctxDispatch(setGraphCreatingEdge(undefined))
          setTimeout(() => network?.addEdgeMode())
        }
      }
    }
  }

  const initDataSets = (nodeValues, edgeValues) => {
    const ns = new DataSet(nodeValues)
    const es = new DataSet(edgeValues)

    setNodeSet(ns)
    setEdgeSet(es)

    return { ns, es }
  }

  useLayoutEffect(() => {
    if (ref.current) {
      const { ns, es } = initDataSets(_.values(nodes), _.values(edges))
      const instance = new Network(ref.current, { edges: es, nodes: ns }, mergedOptions)
      addNetwork(instance);
      instance.fit()
    }
    return () => {
      network?.destroy()
    }
  }, []);

  useEffect(() => {
    if (network) {
      const mergedEvents = {
        ...internalEvents(network, nodeSet, edgeSet),
        click: (items) => handleClickEvt(items, nodeSet, edgeSet),
        doubleClick: (items) => handleDblClickEvt(items, nodeSet)
      }

      for (const key in mergedEvents) {
        const value = mergedEvents[key]
        network.off(key)
        network.on(key, value)
      }
    }
  }, [network, tabIsLocked]);

  useEffect(() => {
    if (network) {
      const nodeDiff = _.difference(_.values(prevGraphData.nodes).map((n) => n.id), _.values(nodes).map((n) => n.id))
      const edgeDiff = _.difference(_.values(prevGraphData.edges).map((e) => e.id), _.values(edges).map((e) => e.id))
      nodeDiff && nodeSet.remove(nodeDiff)
      edgeDiff && edgeSet.remove(edgeDiff)
      nodeSet.update(_.values(nodes))
      edgeSet.update(_.values(edges))
    }
  }, [nodes, edges]);

  useEffect(() => {
    if (isAddingEdge) {
      network?.addEdgeMode()
    } else {
      network?.disableEditMode()
    }
  }, [isAddingEdge])

  useEffect(() => {
    updateGraphNodes(toggleRelationLabels(edges, hideRelationLabels))
  }, [hideRelationLabels])

  const onChangeFilter = (key, value) => {
    return graphFiltersMap[key](value)
  }

  const highlightNodes = (misses) => {
    const toHighlight = _.keys(nodes).filter((id) => misses.indexOf(id) === -1)

    if (toHighlight.length !== 1) {
      network?.view.fit({ nodes: toHighlight, animation: true })
    } else {
      network?.view.focus(toHighlight[0], { scale: 2, locked: true, animation: true })
    }

    if (toHighlight.length !== _.keys(nodes).length) {
      network?.selectNodes(toHighlight, false)
    } else {
      network?.unselectAll()
    }
  }

  const onSearchGraph = (value, hideMisses) => {
    if (value === '') {
      return dispatch(setFilteredNodesBySearch([]))
    }

    const result = _.filter(nodes, (node) =>
      !(node.label.toLowerCase().includes(value.toLowerCase()) || node.wvrId.toLowerCase().includes(value.toLowerCase()))
    ).map((node) => node.id)

    if (hideMisses) {
      dispatch(setFilteredNodesBySearch(result))
    } else {
      dispatch(setFilteredNodesBySearch([]))
      highlightNodes(result)
    }
  }

  const freezeGraph = () => {
    const updatedNodes = {}
    for (const id in nodes) {
      updatedNodes[id] = {
        ...nodes[id],
        physics: isFrozen
      }
    }

    dispatch(setGraphNodes({ nodes: updatedNodes, edges }))
    setIsFrozen(!isFrozen)
  }

  return (
    <GraphViewContainer style={{ height: '100vh' }}>
      <Row align="top" gutter={26}>
        <Col>
          <GraphFilterButton onChangeFilter={onChangeFilter} />
        </Col>
        <Col>
          <SearchGraphButton onSearch={onSearchGraph} />
        </Col>
        <Col>
          <FreezeGraphButton freezeGraph={freezeGraph} isFrozen={isFrozen} />
        </Col>
      </Row>
      <div
        id='graph-container'
        ref={ref}
        style={{
          cursor: isAddingEdge ? 'copy' : 'default',
          position: 'relative',
          width: '100%',
          height: '100vh'
        }}
      />
      {creatingEdgeProps && <CreateRelationModal />}
      {renameRelationProps && <RenameRelationModal />}
    </GraphViewContainer>
  )
}

const mapStateToProps = ({
  explore: {
    currentTab,
    darkModeEnabled,
    tabIsLocked,
    tabsDisabled,
    nameKeys
  },
  graphFilter: {
    hideRelationLabels,
  },
  graph: {
    graphData
  },
}) => ({
  graphData,
  tabsDisabled,
  currentTab,
  darkModeEnabled,
  hideRelationLabels,
  tabIsLocked,
  nameKeys
})

export default connect(mapStateToProps, {
  setCurrentItem,
  setHideRelationLabels,
  setFilteredNodesBySearch,
  setTab,
  setTabDisabled,
  updateGraphNodes
})(Graph)
